import { ArrowDown, Clock3 } from 'lucide-react';
import { useRadarLocale } from '@/i18n/RadarLocaleContext';
import { signalAnchor, watchUpdates, type UpdatePeriod } from './watchUpdates';
import type { RadarWatch } from './types';

type Props = {
  watches: RadarWatch[]; now: Date; period: UpdatePeriod;
  onPeriod: (period: UpdatePeriod) => void; onLocate: (anchor: string) => void;
  hideAttentionCount?:boolean;
};
export function RadarWatchUpdates({watches, now, period, onPeriod, onLocate,hideAttentionCount=false}: Props) {
  const {t, locale} = useRadarLocale();
  const summary = watchUpdates(watches, period, now);
  const groups = new Map<string, {watch: RadarWatch; count: number; first: string}>();
  for (const {watch, signal} of summary.entries) {
    const group = groups.get(watch.id);
    if (group) group.count++;
    else groups.set(watch.id, {watch, count: 1, first: signalAnchor(watch.id, signal.id)});
  }
  const first = summary.entries[0];
  return <section className="radar-updates" aria-label={t('Recent updates')}>
    <div className="radar-updates-heading">
      <div><span className="radar-workspace-eyebrow"><Clock3 size={14} aria-hidden="true"/>{t('Recent updates')}</span>
        <h2>{summary.entries.length ? t(summary.entries.length === 1 ? '{count} update' : '{count} updates', {count: summary.entries.length})
          : t('No updates to review in this period.')}</h2>
        {summary.unconfirmed > 0 && <span className="radar-updates-caution">{t('Needs confirmation · {count}', {count: summary.unconfirmed})}</span>}
      </div>
      <div className="radar-updates-period" role="group" aria-label={t('Discovery period')}>
        <button type="button" aria-pressed={period === 'today'} onClick={() => onPeriod('today')}>{t('Today')}</button>
        <button type="button" aria-pressed={period === 'week'} onClick={() => onPeriod('week')}>{t('Last 7 days')}</button>
      </div>
    </div>
    <div className="radar-updates-status">
      <span>{!watches.some(w => w.status === 'active') ? t('Monitoring paused')
        : summary.latestCheck ? t('Last checked · {date}', {date: new Date(summary.latestCheck).toLocaleString(locale)}) : t('First check pending')}</span>
      {summary.checkedToday > 0 && <span>{t('Companies checked today · {count}', {count: summary.checkedToday})}</span>}
      {summary.running > 0 && <span>{t('Companies being checked · {count}', {count: summary.running})}</span>}
      {!hideAttentionCount&&summary.failed > 0 && <span>{t('Checks needing attention · {count}', {count: summary.failed})}</span>}
      {summary.due > 0 && <span>{t('Companies due for checking · {count}', {count: summary.due})}</span>}
    </div>
    {first && <div className="radar-updates-navigation">
      <button type="button" className="radar-secondary-action" onClick={() => onLocate(signalAnchor(first.watch.id, first.signal.id))}>
        {t('View updates')} <ArrowDown size={16} aria-hidden="true"/>
      </button>
      <div className="radar-updates-companies">{[...groups.values()].map(({watch,count,first}) =>
        <button type="button" key={watch.id} onClick={() => onLocate(first)}>
          {watch.company?.company_name || t('Selected company')} <span>{count}</span><ArrowDown size={13} aria-hidden="true"/>
        </button>)}</div>
    </div>}
    <p className="radar-updates-note">{t('Discovery time in your timezone. Counts cover distinct updates for active companies; company background, earlier-focus and dismissed entries are excluded.')}</p>
  </section>;
}
